/** Теги, содержимое которых стоит на своей строке — за ними при чтении текста граница строки */
const BLOCK_LEVEL_TAGS = new Set([
  'P',
  'DIV',
  'UL',
  'OL',
  'LI',
  'TABLE',
  'TR',
  'BLOCKQUOTE',
  'PRE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
]);

const isBlockLevelElement = (node: Node | null): boolean =>
  node?.nodeType === Node.ELEMENT_NODE && BLOCK_LEVEL_TAGS.has((node as Element).tagName);

/** Соседство, рядом с которым пробельный пропуск уже ничего не разделяет: строка и так кончилась */
const isLineBreakingNeighbour = (node: Node | null): boolean =>
  isBlockLevelElement(node) || (node?.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'BR');

/** Разбирает разметку в документ — общий первый шаг для всех переводов её в текст. */
const parseRichText = (html: string): HTMLElement =>
  new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html').body;

/**
 * Теги, содержимое которых — одна строка текста. Пустыми они значат строку, оставленную пустой,
 * тогда как пустой `ul` или `li`-контейнер — лишь остаток разметки.
 */
const TEXT_LINE_TAGS = new Set(['P', 'DIV', 'LI']);

/**
 * Строка, оставленная в тексте пустой: в разметке она записана блоком без содержимого.
 *
 * Редактор держит такую строку переносом внутри блока, но в сохранённый текст перенос не попадает —
 * блок остаётся пустым, и на экране высоты не занимает, если ему не вернуть перенос.
 */
export const isBlankRichTextLine = (element: Element): boolean =>
  TEXT_LINE_TAGS.has(element.tagName) && element.childElementCount === 0 && !element.textContent?.trim();

/**
 * Пропуск, набранный отступами самой разметки, а не написанный в тексте.
 *
 * Границы строк в тексте заданы переносами, и различить их с отступами между тегами можно только
 * по соседству: пропуск на краю содержимого или рядом с началом новой строки ничего не разделяет,
 * а тот же перенос между словами и начертаниями — граница строки, написанная автором.
 */
export const isRichTextLayoutWhitespace = (node: Text): boolean => {
  if ((node.nodeValue ?? '').trim()) return false;

  const { previousSibling, nextSibling } = node;
  if (!previousSibling || !nextSibling) return true;

  return isLineBreakingNeighbour(previousSibling) || isLineBreakingNeighbour(nextSibling);
};

/**
 * Текст комментария без разметки в одну строку — то, по чему сравниваются версии.
 * Границы абзацев не сохраняются: для сравнения важны сами слова.
 */
export const richTextToPlain = (html: string): string => parseRichText(html).textContent ?? '';

/** Накопитель текста: границы строк ставятся с оглядкой на уже набранное */
type ReadableTextBuilder = {
  text: string;
};

/**
 * Граница строки, заданная разметкой. Подряд идущие границы схлопываются: соседние абзацы
 * стоят на своих строках, но пустой строкой не разделяются — как и на экране.
 */
const appendBlockBoundary = (builder: ReadableTextBuilder): void => {
  if (!builder.text || builder.text.endsWith('\n')) return;
  builder.text += '\n';
};

const appendReadableNode = (node: Node, builder: ReadableTextBuilder): void => {
  if (node.nodeType === Node.TEXT_NODE) {
    if (!isRichTextLayoutWhitespace(node as Text)) builder.text += node.nodeValue ?? '';
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const element = node as Element;
  // Перенос разметкой поставлен намеренно — схлопывать его с границей блока нечего
  if (element.tagName === 'BR') {
    builder.text += '\n';
    return;
  }

  const isBlock = BLOCK_LEVEL_TAGS.has(element.tagName);
  if (isBlock) appendBlockBoundary(builder);
  for (const child of element.childNodes) appendReadableNode(child, builder);
  if (isBlock) appendBlockBoundary(builder);
};

/**
 * Текст комментария для чтения: разметка снята, но границы абзацев и строк сохранены переносами.
 * Без них конец одного абзаца слипается с началом следующего в одно слово.
 *
 * Переносы, написанные в самом тексте, — такие же границы строк, как и разметка: текст, набранный
 * строками без единого тега, читается построчно и здесь, и на экране ({@link isRichTextLayoutWhitespace}).
 */
export const richTextToReadableText = (html: string): string => {
  const builder: ReadableTextBuilder = { text: '' };
  for (const node of parseRichText(html).childNodes) appendReadableNode(node, builder);

  // Пустой абзац между текстом даёт двойной перенос — больше него отступ уже не читается
  return builder.text.replace(/\n{3,}/g, '\n\n').trim();
};

/** Знаки, которые в разметке значат не себя: текст с ними в разметку переносится экранированным */
const escapeRichTextChars = (text: string): string => {
  const holder = document.createElement('div');
  holder.textContent = text;
  return holder.innerHTML;
};

/**
 * Текст без разметки: ни тегов, ни экранированных знаков — его можно переносить в редактор как есть.
 *
 * Разметка от простого текста отличается только тем, что при чтении меняется: у текста без неё
 * прочитанное совпадает с исходным знак в знак.
 */
export const isPlainText = (value: string): boolean => richTextToPlain(value) === value;

/**
 * Простой текст в разметку: границы строк, написанные переносами, становятся переносами разметки.
 *
 * Нужен тексту, пришедшему без разметки (составленному системой или сохранённому до того, как
 * его стали набирать с оформлением): редактор разметки принимает пропуски по правилам HTML и
 * схлопнул бы такие переносы в пробел — набранные строки пропали бы при первой же правке.
 */
export const plainTextToRichText = (text: string): string => escapeRichTextChars(text).replace(/\r?\n/g, '<br>');
